import * as React from 'react';

import { Translate } from 'react-localize-redux';

import styled, { css } from 'styled-components';

import { StreamingPlatform } from '@common/types/data/streaming-platform';
import { Option } from '@webapp/types/common/option';

import { AnimatePresence } from 'framer-motion';

import { GenericDropdownBuilder } from '@webapp/controllers/form/dropdown';

import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';
import CircleCheckIcon from '@components/basics/icons/circle-check';
import MotionOpacity from '@components/motion/motion-opacity';
import UnselectedIcon from '@components/basics/icons/unselected';

import DecorationDropdown from '../decoration-dropdown';

import SpotifyIsologo from '@components/basics/streaming-isologos/spotify';
import AppleMusicIsologo from '@components/basics/streaming-isologos/apple';
import YouTubeMusicIsologo from '@components/basics/streaming-isologos/youtube';
import AmazonMusicIsologo from '@components/basics/streaming-isologos/amazon';
import TidalIsologo from '@components/basics/streaming-isologos/tidal';
import SoundcloudIsologo from '@components/basics/streaming-isologos/soundcloud';
import AppState from '@webapp/store/state';
import { connect } from 'react-redux';

interface StreamingPlatformInputCtrlProps {
  className?: string;
  value?: string;
  platform: StreamingPlatform['platform'];
  hasClose?: boolean;
  noDropdown?: boolean;
  lockChangeProfile?: boolean;
  onClose?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onChange: (value: string) => void;
  onPlatformChange: (value: StreamingPlatform['platform']) => void;
  disableSelect?: boolean;
  errorMessage?: React.ReactNode;
  isLaptop?: boolean;
}

const mapStateToProps = (state: AppState) => ({ isLaptop: state.ui.laptop });

const platformValidationDomain: Record<string, string> = {
  spotify: 'open.spotify.com',
  apple: 'music.apple.com',
  appleMusic: 'music.apple.com',
  itunes: 'music.apple.com',
  youtube: 'youtube.com',
  youtubeMusic: 'music.youtube.com',
  google: 'google',
  googleStore: 'store.google.com',
  amazon: 'music.amazon.com',
  amazonMusic: 'music.amazon.com',
  amazonStore: 'amazon.com',
  tidal: 'tidal.com',
  soundcloud: 'soundcloud.com',
  snapchat: 'snapchat.com',
  facebook: 'facebook.com',
  twitter: 'twitter.com',
  instagram: 'instagram.com',
  tiktok: 'vm.tiktok.com',
  linkedin: 'linkedin.com',
  napster: 'us.napster.com',
  deezer: 'deezer.com',
  pandora: 'pandora.com',
  yandex: 'yandex.ru',
  spinrilla: 'spinrilla.com',
  custom: '',
};

const buildValidationRegExp = (platform: string): RegExp => {
  const domain = platformValidationDomain[platform];

  if (!domain) {
    return new RegExp('');
  }

  const allowWWW = domain.split('.').length < 3;
  const www = allowWWW ? '(www.)?' : '';
  return new RegExp(`^https?://${www}${domain}`, 'i');
};

export const getPlatformDomain = (platform: string): string => {
  const domain = platformValidationDomain[platform];

  if (!domain) {
    return '';
  }

  return `https://${domain}`;
};

export const checkStartWith = (
  platform: string,
  stringToCheck: string
): boolean => {
  const regExp = buildValidationRegExp(platform);
  return regExp.test(stringToCheck);
};

export const STREAMING_PLATFORMS: Option<StreamingPlatform['platform']>[] = [
  {
    value: 'spotify',
    label: 'Spotify',
    decoration: <SpotifyIsologo className="platform-btn" />,
    validate: (value: string) => checkStartWith('spotify', value),
  },
  {
    value: 'apple',
    label: 'Apple Music',
    decoration: <AppleMusicIsologo className="platform-btn" />,
    validate: (value: string) => checkStartWith('apple', value),
  },
  {
    value: 'youtube',
    label: 'YouTube',
    decoration: <YouTubeMusicIsologo className="platform-btn" />,
    validate: (value: string) => checkStartWith('youtube', value),
  },
  {
    value: 'amazon',
    label: 'Amazon Music',
    decoration: <AmazonMusicIsologo className="platform-btn" />,
    validate: (value: string) => checkStartWith('amazon', value),
  },
  {
    value: 'tidal',
    label: 'Tidal',
    decoration: <TidalIsologo className="platform-btn" />,
    validate: (value: string) => checkStartWith('tidal', value),
  },
  {
    value: 'soundcloud',
    label: 'SoundCloud',
    decoration: <SoundcloudIsologo className="platform-btn" />,
    validate: (value: string) => checkStartWith('soundcloud', value),
  },
];

const DropdownCtrl = GenericDropdownBuilder<StreamingPlatform['platform']>();

export const StreamingPlatformInputCtrl: React.FC<
  StreamingPlatformInputCtrlProps
> = (props) => {
  const [isOpen, setOpen] = React.useState(false);
  const [isNew, setNew] = React.useState(false);

  React.useEffect(() => {
    if (props.value === 'NEW') {
      if (!isNew) {
        setNew(true);
      }
    } else {
      setNew(false);
    }
  }, [props.platform, props.value, isNew]);

  const currentOption = STREAMING_PLATFORMS.find(
    (s) => s.value === props.platform
  ) as Option<StreamingPlatform['platform']>;

  const handleNew = () => {
    if (props.lockChangeProfile) {
      return;
    }
    if (isNew) {
      props.onChange('');
    } else {
      props.onChange('NEW');
    }

    setNew(!isNew);
  };

  const leftDecoration = (
    <StyledDecorationDropdown
      isNew={isNew}
      listOpen={isOpen}
      hideArrow={props.disableSelect}
    >
      {currentOption.decoration}
    </StyledDecorationDropdown>
  );

  const rightDecoration = (
    <Flex justifyContent="flex-end" alignItems="center" onClick={handleNew}>
      <AnimatePresence>
        {!isNew ? (
          <MotionOpacity>
            <CheckboxTitle
              weight="700"
              type={props.isLaptop ? 'small' : undefined}
              isNew={isNew}
            >
              <Translate id="STREAMING.INPUT.CHECKBOX" />
            </CheckboxTitle>
          </MotionOpacity>
        ) : null}
      </AnimatePresence>
      {isNew ? (
        <CircleCheckIcon dark className="check-icon" iconSize={24} />
      ) : (
        <UnselectedIcon className="check-icon" iconSize={24} />
      )}
    </Flex>
  );

  return (
    <DropdownCtrl
      className={props.className}
      title=""
      value={props.platform}
      onChange={(value) => props.onPlatformChange(value)}
      options={STREAMING_PLATFORMS}
      onOpen={setOpen}
      disabled={props.disableSelect}
      header={
        <>
          <StyledInputField
            isNew={isNew}
            name="input-platform"
            label={`${currentOption.label} Profile Link`}
            leftDecoration={leftDecoration}
            rightDecoration={!props.value || isNew ? rightDecoration : null}
            hasClose={props.hasClose}
            onClose={props.onClose}
            locked={props.lockChangeProfile || (!isNew ? !!props.value : false)}
            error={props.errorMessage}
            autoComplete="off"
            value={!isNew ? props.value : ''}
            onChange={(value) => props.onChange(value)}
          />
          {isNew ? (
            <MotionOpacity>
              <PositionedType weight="700">
                Create new {currentOption.label} profile
              </PositionedType>
            </MotionOpacity>
          ) : null}
        </>
      }
    />
  );
};

export default connect(mapStateToProps)(styled(StreamingPlatformInputCtrl)`
  position: relative;
  .platform-btn {
    cursor: ${(props) => (props.disableSelect ? 'auto' : 'pointer')};
    width: 20px;
    height: auto;
    max-height: 20px;
    padding: 0 4px 4px 0;

    @media ${({ theme }) => theme.device.tablet} {
      width: 24px;
      height: auto;
      padding: 0;
    }
  }

  .check-icon {
    cursor: pointer;
  }

  ${(props) =>
    props.disableSelect
      ? css`
          .decoration-left {
            pointer-events: none;
            margin-left: 0px;
            &:before {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 20px;
              height: 20px;
              background-color: ${({ theme }) => theme.colors.header};
              border-radius: 4px;
            }
            @media ${({ theme }) => theme.device.tablet} {
              margin-left: 4px;
              &:before {
                width: 28px;
                height: 28px;
              }
            }
            @media ${({ theme }) => theme.device.laptopXL} {
              margin-left: 0;
            }
          }
          .platform-btn {
            cursor: auto;
            width: 12px;
            max-height: 12px;
            padding: 0;
            @media ${({ theme }) => theme.device.tablet} {
              width: 16px;
              max-height: 16px;
            }
          }
        `
      : null}
`);

const StyledDecorationDropdown = styled(DecorationDropdown)<{
  isNew?: boolean;
}>`
  ${({ isNew }) =>
    isNew
      ? css`
          .platform-btn {
            & > * {
              fill: ${({ theme }) => theme.colors.primary};
            }
          }
        `
      : null}
`;

const CheckboxTitle = styled(Typography)<{ isNew?: boolean }>`
  margin-right: 10px;
  max-width: 48px;
  text-align: right;
  @media ${({ theme }) => theme.device.laptop} {
    max-width: none;
  }

  ${({ isNew }) =>
    isNew
      ? css`
          display: none;
        `
      : null}
`;

const StyledInputField = styled(InputField)<{ isNew?: boolean }>`
  ${({ isNew }) =>
    isNew
      ? css`
          background-color: ${({ theme }) => theme.colors.primary};

          input {
            background-color: ${({ theme }) => theme.colors.primary};
          }

          label {
            opacity: 0;
          }

          .decoration-left {
            &:before {
              background-color: ${({ theme }) => theme.colors.background};
            }
          }
        `
      : null}
`;

const PositionedType = styled(Typography)`
  position: absolute;
  top: 50%;
  left: 45px;
  transform: translate(0, -50%);
  z-index: 999;
  color: ${({ theme }) => theme.colors.background};
`;
